/**
 * Main-weapon families (อาวุธหลัก): the one table that says what kind of weapon an art is fought with, replacing the
 * scattered `art.weapon === 'sword' | 'unarmed'` checks (docs/design/combat/2026-10-11-weapon-arts.md §5). Data only.
 *
 * Today three families: `sword` (jian and the dao sabres, one family until the sabre is split), `unarmed` (fist and palm) and
 * the prototype `staff` (ไม้พลอง). More (spear, fans, chain…) are one row each. Code asks `familyOf` / `isArmed`, never the id.
 */
export type Weapon = 'sword' | 'unarmed' | 'staff';

export interface WeaponFamily {
  readonly id: Weapon;
  /** Thai label for the arts panel. */
  readonly label: string;
  /** The hand holds a weapon item (a fist fighter's weapon slot is emptied). */
  readonly armed: boolean;
  /** Basic attack range of a typical art of the family, metres (docs only; each art's own `basic.range` rules). */
  readonly basicRange: number;
  /** Bot: how far its basic blow counts as in reach for engaging (metres). */
  readonly botReach: number;
  /** Bot: the least distance it keeps while spacing (metres); a long weapon holds off the foe, the inside is its weak spot. */
  readonly botFloor: number;
  /** Multiplies the damage a fighter of this family takes (1 = none). A long weapon's lighter, slower trade keeps a bout's length in the normal band (balance R8). */
  readonly takenMul: number;
}

export const WEAPON_FAMILIES: Readonly<Record<Weapon, WeaponFamily>> = {
  sword: { id: 'sword', label: 'กระบี่', armed: true, basicRange: 2.1, botReach: 2, botFloor: 1.5, takenMul: 1 },
  unarmed: { id: 'unarmed', label: 'มือเปล่า', armed: false, basicRange: 1.7, botReach: 2, botFloor: 1.5, takenMul: 1 },
  staff: { id: 'staff', label: 'ไม้พลอง', armed: true, basicRange: 2.8, botReach: 2.8, botFloor: 2.1, takenMul: 0.75 },
};

/** The family of an art (or of a bare family id). */
export function familyOf(a: { readonly weapon: Weapon } | Weapon): WeaponFamily {
  return WEAPON_FAMILIES[typeof a === 'string' ? a : a.weapon];
}

/** Holds a weapon item (a sword, a staff…), as opposed to bare hands. */
export function isArmed(a: { readonly weapon: Weapon } | Weapon): boolean {
  return familyOf(a).armed;
}

/** The straight-sword family (กระบี่ and ดาบ arts). */
export function isSwordFamily(a: { readonly weapon: Weapon } | Weapon): boolean {
  return familyOf(a).id === 'sword';
}
